/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, ReactNode } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import SidenavCollapse from "layouts/navigation/Sidenav/SidenavCollapse";
import SidenavList from "layouts/navigation/Sidenav/SidenavList";
import SidenavItem from "layouts/navigation/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "layouts/navigation/Sidenav/SidenavRoot";
import sidenavLogoLabel from "layouts/navigation/Sidenav/styles/sidenav";

import { IntlShape } from "react-intl";
import { IntlContext } from "index";

// axios
import axios from 'axios';
import { apiClient } from 'api-client';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { setLoggedUser } from 'redux/actions/usersActions';
import { setRefresh } from 'redux/actions/pageActions';
import { deleteAllFilters, resetOffset } from 'redux/actions/tableActions';
import { deleteFormItems } from 'redux/actions/priceActions';

// extracted Data
import { extractDataUserLoggedIn } from 'common/extractData';

// types
import { UserLoggedType } from 'types/types';
import { FilterState } from 'types/typesRedux';

import '../../../css/language.css';
// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { flexbox } from "@mui/system";

type Intlcontext = {
  locale?: string,
  messages?: Record<string, string>,
  selectedLanguage: string,
  handleLanguageChange: (newLocale: string) => {},
}

// Declaring props types for Sidenav
interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName?: string;
  routes: {
    [key: string]:
      | ReactNode
      | string
      | {
          [key: string]:
            | ReactNode
            | string
            | {
                [key: string]: ReactNode | string;
              }[];
        }[];
  }[];
  [key: string]: any;
  intl: IntlShape,
}

type userLoggedData = {
  userLogged: { userLogged: UserLoggedType };
}

// type Refresh = {
//   handleRefresh: RefreshState  
// }

function Sidenav({ intl, color, brand, brandName, routes, ...rest }: Props): JSX.Element {
  
  const filters = useSelector( (state: FilterState) => state.filters);
  const pricesFines = useSelector( (state: any) => state.pricesList.pricesFines )  
  const pricesForm = useSelector( (state: any) => state.pricesList.pricesForm )  
  
  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
  const [controller, dispatchch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  let textColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark"
    | "white"
    | "inherit"
    | "text"
    | "light" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const dispatch = useDispatch();

  const user = useSelector( (state: userLoggedData) => state.userLogged.userLogged )

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // handle get logged user
  const getLoggedUser = async () => {
    const cancelToken = axios.CancelToken.source()

    try {  
      const response = await apiClient.get(`/v1/me/profile`,{
        cancelToken:cancelToken.token
      } );
        if ( response ) {
          let responseData = extractDataUserLoggedIn(intl)( response.data )
          dispatch(setLoggedUser( responseData ));
        }

      } catch (err) {
        if (axios.isCancel(err)) {
          console.log("canceled")
        } else {
          // todo:handle error
        }
      }
    return () => cancelToken.cancel()
  }

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  // if open side nav get user logged in
  useEffect(() => {
    getLoggedUser()
  }, []);


  const handleLanguage = ( lang: string ) => {
    localStorage.setItem( 'lang', lang );
    dispatch(setRefresh( { refreshLanguage: true } ))
  }

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatchch, window.innerWidth < 0);
      // setMiniSidenav(dispatchch, true);
      // setTransparentSidenav(dispatchch, window.innerWidth < 1200 ? false : transparentSidenav);
      // setWhiteSidenav(dispatchch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatchch, location]);

 
  const navLinkStyle: React.CSSProperties = {
    // backgroundColor: '#4BA64F',
    backgroundColor: '#D9D9D9',
  };

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: any) => {
    const template = collapse.map(({ name, route, key, href }: any) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses: any) =>
    collapses.map(({ name, collapse, route, href, key }: any) => {
      let returnValue;
      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }: any) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}            
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });
  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route, adress }: any) => {
      let returnValue;            
      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none", }}
            >
              <SidenavCollapse
                className="sidenav"
                name={name}
                icon={icon}
                // active={key === collapseName}
                active={ key === itemParentName }
                noCollapse={noCollapse}

              />
            </Link>
          ); } else 
          if (noCollapse && route) {
          returnValue = (
            <NavLink to={adress + route} key={key} style={navLinkStyle}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                // active={key === collapseName}
                active={key === itemParentName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } 
        else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }
      return returnValue;
    }
  );

  // If click sideNav link, delete all filters and form and set tables offset to 0
  const handleFilter = () => {
    if ( Object.keys(filters).length !== 0 ) dispatch(deleteAllFilters());
    if ( pricesForm.equipment.length > 0 ) dispatch(deleteFormItems( 'equipment' ));
    if ( pricesForm.fines.length > 0 ) dispatch(deleteFormItems( 'fines' ));
    dispatch(resetOffset());
  }

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        {/* <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox> */}
        <MDBox
        //  component={NavLink} to="/" 
         display="flex" alignItems="center">
          {brand && 
          <MDBox component="img" src={brand} alt="Brand" width="11.6rem" marginLeft="0rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            {/* <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}
              sx={{ paddingLeft: "0.7rem" }}
            >
              {brandName}
            </MDTypography> */}
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <MDBox 
        sx={{ 
          display:"flex", flexDirection:"row", 
          justifyContent:"center", fontSize:"0.875rem",
          color: whiteSidenav ? "#344767" : "#ffffff",
        }}
      > 
      {user.email} 
      </MDBox>
      <MDBox 
        sx={{ 
          display:"flex", flexDirection:"row", 
          justifyContent:"center", fontSize:"0.875rem", 
          color: whiteSidenav ? "#344767" : "#ffffff",
        }}
      > 
      pin: {user.pin ? user.pin : "-"} 
      </MDBox>

      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

      <List onClick= { handleFilter } >{renderRoutes}</List>

      {/* <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

      <IntlContext.Consumer>{(intlContextProps: Intlcontext) => (
        <MDBox className='languagesWrapper'
          sx={{ display:"flex", flexDirection:"row", width:"100%",
            justifyContent:"center", gap:"0.5rem",}}
        >
          <MDTypography
            className={`${intlContextProps.selectedLanguage === 'sk' ? 'selected' : 'unselected'} language`}
            sx={{ 
              cursor:"pointer",
              color: whiteSidenav ? "#344767" : "#ffffff", 
            }}
            onClick={() => {
              if (intlContextProps.selectedLanguage !== "sk") {
                intlContextProps.handleLanguageChange("sk");
                handleLanguage("sk");
              }
            }}
          >
            SK
          </MDTypography>
          <MDTypography
            className={`${intlContextProps.selectedLanguage === 'en' ? 'selected' : 'unselected'} language`}
            sx={{ 
              cursor:"pointer",
              color: whiteSidenav ? "#344767" : "#ffffff",
            }}
            onClick={() => {
              if (intlContextProps.selectedLanguage !== "en") {
                intlContextProps.handleLanguageChange("en");
                handleLanguage("en");
              }
            }}
          >
            EN
          </MDTypography>
        </MDBox>
      )}
      </IntlContext.Consumer> */}

      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

    </SidenavRoot>
  );
}

// Declaring default props for Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

export default Sidenav as any;
